import { CsAsset } from '../../../common/models/content-studio.model';
import { CanvasNode, CanvasRecipeNode } from '../content-studio/canvas/canvas-graph.types';
import { ProductNodeProduct } from '../content-studio/canvas/ProductNode';
import { StudioBoard } from './studio-contract';

/**
 * The same production, as canvas nodes.
 *
 * A board opened on the canvas used to come up empty: the project's graph
 * defaults to an empty list, which the canvas treats as "the vendor cleared
 * it". Now an empty graph on a project that has a board is built from that
 * board, one row per shot:
 *
 *   product → shot (studio-still) → photo, earlier photos → film (studio-clip) → films
 *
 * The recipe nodes carry the shot's settings, so a shot can be re-run or
 * changed on the canvas; the generation nodes are the real assets, so every
 * canvas tool (retouch, expand, video, design) works on them.
 */

const ROW = 420;
const COL = 330;

export function boardToCanvas(
    board: StudioBoard,
    assets: Map<string, CsAsset>,
    products: Map<string, ProductNodeProduct>,
): CanvasNode[] {
    const nodes: CanvasNode[] = [];
    const productNodeIds = new Map<string, string>();

    board.shots.forEach((shot, row) => {
        const y = 80 + row * ROW;
        const hero = shot.productUuids[0];
        let productNodeId = '';
        if (hero) {
            productNodeId = productNodeIds.get(hero) || '';
            if (!productNodeId) {
                productNodeId = `product-${hero}`;
                productNodeIds.set(hero, productNodeId);
                nodes.push({
                    kind: 'product', id: productNodeId, x: 80, y,
                    products: shot.productUuids.map((u) => products.get(u) || ({ uuid: u, name: u.slice(0, 8) } as ProductNodeProduct)),
                });
            }
        }

        const still: CanvasRecipeNode = {
            kind: 'recipe',
            id: `shot-${shot.id}`,
            x: 340,
            y,
            productNodeId,
            recipeSlug: 'studio-still',
            inputs: {
                look: board.look, format: board.format, scene: shot.scene, camera: shot.camera,
                cameraNote: shot.cameraNote, framingRef: shot.framingRef, action: shot.action, kind: shot.kind,
                title: shot.title,
            },
            generationNodeIds: [],
            generating: false,
            collapsed: true,
        };
        nodes.push(still);

        let x = 340 + COL;
        const photos = [shot.still?.assetUuid, ...(shot.still?.takes || []).map((t) => t.assetUuid)].filter(Boolean) as string[];
        let lastPhotoId = '';
        for (const uuid of photos) {
            const asset = assets.get(uuid);
            if (!asset || asset.status !== 'ready') continue;
            const id = `gen-${uuid}`;
            still.generationNodeIds.push(id);
            nodes.push({ kind: 'generation', id, x, y, asset, recipeNodeId: still.id, approved: uuid === shot.still?.assetUuid });
            if (uuid === shot.still?.assetUuid) lastPhotoId = id;
            x += COL;
        }

        const films = [shot.clip?.assetUuid, ...(shot.clip?.takes || []).map((t) => t.assetUuid)].filter(Boolean) as string[];
        if (films.length && lastPhotoId) {
            const clip: CanvasRecipeNode = {
                kind: 'recipe',
                id: `film-${shot.id}`,
                x,
                y,
                productNodeId: lastPhotoId,
                recipeSlug: 'studio-clip',
                inputs: { look: board.look, format: board.format, motion: shot.motion, durationSec: shot.durationSec, engine: shot.engine, action: shot.action },
                generationNodeIds: [],
                generating: false,
                collapsed: true,
            };
            nodes.push(clip);
            x += COL;
            for (const uuid of films) {
                const asset = assets.get(uuid);
                if (!asset || asset.status !== 'ready') continue;
                const id = `gen-${uuid}`;
                clip.generationNodeIds.push(id);
                nodes.push({ kind: 'generation', id, x, y, asset, recipeNodeId: clip.id, approved: uuid === shot.clip?.assetUuid });
                x += COL;
            }
        }
    });
    return nodes;
}
